import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import SeoMeta from "@/components/SeoMeta";
import ShareButton from "@/components/ShareButton";
import FarmersNav from "@/components/FarmersNav";
import FarmersIntroCard from "@/components/FarmersIntroCard";
import RelatedReading from "@/components/RelatedReading";
import FarmersWebsitePreview from "@/components/FarmersWebsitePreview";
import FarmersHomeHero from "@/farmers_pulse/standalone/HomeHero";
import {
  Sun, CloudRain, Plant, Warning, PottedPlant, Leaf, Drop, Bug, UsersThree, House,
  Notebook, Calendar, Newspaper, Sparkle, MapPin, ArrowRight, Plus, Books, Storefront,
} from "@phosphor-icons/react";

/**
 * FarmersPulseHome — the modular landing at /farmers.
 * All cards are rendered from the backend module registry
 * (`GET /api/farmers/modules`) so admins can toggle, reorder, or scope
 * them by geography without touching this file.
 */

const ICON_MAP = {
  sun: Sun, "cloud-rain": CloudRain, plant: Plant, warning: Warning,
  seed: PottedPlant, leaf: Leaf, drop: Drop, bug: Bug, users: UsersThree, house: House,
  notebook: Notebook, calendar: Calendar, newspaper: Newspaper, sparkle: Sparkle,
};

export default function FarmersPulseHome() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loc, setLoc] = useState(() => {
    try {
      const raw = localStorage.getItem("np:pulse-location");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [nearby, setNearby] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [myFarms, setMyFarms] = useState([]);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    http.get("/cms/pages", { params: { page_type: "articles", tag: "farmers-pulse", limit: 12 } })
      .then(({ data }) => setGuides(data.pages || [])).catch(() => {});
  }, []);

  const isStandalone = process.env.REACT_APP_FARMERSPULSE_STANDALONE === "true";

  // Load modules once.
  useEffect(() => {
    http.get("/farmers/modules")
      .then(({ data }) => setModules(data.modules || []))
      .catch(() => toast.error("Could not load Farmers Pulse modules"))
      .finally(() => setLoading(false));
  }, []);

  // Load calendar + nearby whenever we have coordinates.
  useEffect(() => {
    if (!loc?.lat || !loc?.lon) return;
    http.get(`/farmers/calendar?latitude=${loc.lat}&longitude=${loc.lon}`)
      .then(({ data }) => setCalendar(data)).catch(() => {});
    http.get(`/farmers/observations/nearby?latitude=${loc.lat}&longitude=${loc.lon}&radius_km=50&limit=12`)
      .then(({ data }) => setNearby(data.observations || [])).catch(() => {});
  }, [loc?.lat, loc?.lon]);

  // Load my farms if logged in.
  useEffect(() => {
    if (!user) return;
    http.get("/farmers/farms/mine")
      .then(({ data }) => setMyFarms(data.farms || []))
      .catch(() => {});
  }, [user]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      const l = { lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" };
      setLoc(l);
      try { localStorage.setItem("np:pulse-location", JSON.stringify(l)); } catch { /* noop */ }
    }, () => toast.error("Could not read your location. You can still browse."));
  };

  const enabledByKey = useMemo(() => {
    const map = {};
    for (const m of modules) map[m.key] = m;
    return map;
  }, [modules]);

  const heroTxt = "Farmers Pulse — a living almanac of what is happening on farms around the world. Local weather, crop calendars, pests and community observations, all in one place.";

  // Show a "submitted — pending review" banner when we come back from
  // /farmers/report?submitted=1 (moderation queue means the report isn't
  // visible on the feed yet).
  const showSubmittedBanner = typeof window !== "undefined" && window.location.search.includes("submitted=1");

  return (
    <main
      className={`bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen ${
        isStandalone ? "pt-0" : "pt-32 md:pt-36"
      } pb-32`}
      data-testid="farmers-pulse-home"
    >
      <SeoMeta
        title="Farmers Pulse — the farmer's daily companion"
        description="Crop guides, market prices, farm weather, mandi rates and a knowledge desk — practical, field-tested farming intelligence from the NaturePulse network."
        canonical={`${window.location.origin}/farmers`}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Farmers Pulse", url: `${window.location.origin}/farmers` }}
      />
      {/* New hero — Rhythm of Modern Farming — top of the page. */}
      <FarmersHomeHero />

      {!isStandalone && <FarmersNav />}
      {/* Living Almanac intro — editorial block below the hero */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14"
        data-testid="farmers-almanac-intro"
      >
        <div className="rounded-3xl border border-black/10 bg-white/70 backdrop-blur px-6 md:px-10 py-8 md:py-10">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
            <div className="md:sticky md:top-40 space-y-1.5">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-1.5">
                <Books size={12} weight="fill" /> Farmers Pulse
              </div>
              <div className="font-serif-display text-3xl md:text-4xl leading-[1.02] tracking-tight max-w-xs">
                The Living Almanac for Farmers Everywhere
              </div>
            </div>
            <div className="text-[#241209]">
              <p className="text-base md:text-lg leading-relaxed">
                For thousands of years, farmers have looked to the sky, the soil, plants, birds, insects, seasons, and generations of accumulated knowledge to decide when to plant, when to irrigate, when to harvest, and when to prepare for changing conditions.
              </p>
              <p className="mt-4 text-base md:text-lg leading-relaxed">
                Traditional farmers' almanacs helped organize much of this knowledge around seasons, weather, planting cycles, the sun, the moon, and the calendar. At <strong>NaturePulse.net</strong>, we want to take that idea much further.
              </p>
              <p className="mt-4 text-base md:text-lg leading-relaxed">
                We are introducing <strong>Farmers Pulse</strong> — a growing global agricultural information, observation, and knowledge platform designed to help farmers understand not only what normally happens in their region, but also what is happening around them right now.
              </p>
              <p className="mt-4 font-serif-display text-xl md:text-2xl italic text-[#1B4332]">
                From the Traditional Almanac to a Living Almanac.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Link
                  to="/farmers/almanac"
                  data-testid="farmers-intro-read-more"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
                >
                  Read the full story <ArrowRight size={11} weight="bold" />
                </Link>
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                  8 min read · Observe · Record · Learn · Grow · Share
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick action bar — surface CTAs + location, works after the marketing hero */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10">
        {showSubmittedBanner && (
          <div
            data-testid="farmers-submitted-banner"
            className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 flex items-start gap-3"
          >
            <Sparkle size={18} weight="fill" className="mt-0.5" />
            <div className="flex-1">
              <div className="font-serif-display text-lg leading-tight">Report submitted — thank you.</div>
              <div className="mt-0.5 text-xs">
                It's queued for community review. Once verified, it will appear on the map and nearby feed.
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <ListenTranslateBar text={heroTxt} testidPrefix="farmers-hero-tools" />
          <ShareButton
            title="Farmers Pulse — NaturePulse"
            text="Local weather · crop calendars · community observations from farmers everywhere."
            hashtags={["FarmersPulse", "NaturePulse"]}
            variant="chip"
            testid="farmers-hero-share"
          />
          <Link
            to="/farmers/report"
            data-testid="farmers-hero-report"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> Report what you see
          </Link>
          <Link
            to="/farmers/farms/new"
            data-testid="farmers-hero-add-farm"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            <House size={11} weight="fill" /> Add a farm
          </Link>
          <Link
            to="/farmers/websites"
            data-testid="farmers-hero-build-website"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Storefront size={11} weight="fill" /> Build a website
          </Link>
        </div>

        {/* Location strip */}
        <div className="mt-4 flex items-center gap-3 flex-wrap rounded-full border border-black/10 bg-white/70 backdrop-blur px-4 py-2">
          <MapPin size={14} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
          <span className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-[#3A3830]">
            {loc?.label ? loc.label : "Location not set"}
            {loc?.lat && <span className="ml-2 text-[#3A3830]">{loc.lat.toFixed(2)}, {loc.lon.toFixed(2)}</span>}
          </span>
          <button
            onClick={useMyLocation}
            data-testid="farmers-use-location"
            className="ml-auto font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
          >
            {loc ? "Update location" : "Use my location"}
          </button>
        </div>
      </section>

      {/* Live preview of the farmer's website(s) — or CTA to build one. */}
      <FarmersWebsitePreview />

      {/* Modular grid */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10" data-testid="farmers-modules">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-2xl border border-black/10 bg-white/60 h-48 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {modules.map((m) => (
              <ModuleCard
                key={m.id}
                module={m}
                loc={loc}
                calendar={calendar}
                nearby={nearby}
                myFarms={myFarms}
                signedIn={!!user}
              />
            ))}
          </div>
        )}
      </section>

      {/* Field guides from the Farmers Pulse desk */}
      {guides.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14" data-testid="farmers-guides">
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Field guides</div>
              <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight">Practical guides from the Farmers Pulse desk.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {guides.map((g) => (
              <Link key={g.slug} to={`/read/articles/${g.slug}`} data-testid={`farmers-guide-${g.slug}`}
                className="group rounded-2xl border border-black/10 bg-white p-5 hover:border-[#1B4332] hover:-translate-y-0.5 transition-all">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#1B4332]/70">{g.category || "Guide"}</div>
                <div className="mt-1.5 font-serif-display text-xl leading-tight">{g.title}</div>
                <p className="mt-2 text-sm text-[#3A3830] line-clamp-2">{g.excerpt || g.subtitle}</p>
                <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  Read the guide <ArrowRight size={10} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Intro article beneath modules */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14">
        <FarmersIntroCard />
      </section>

      {/* Footer strip */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-12">
        <div className="rounded-2xl border border-black/10 bg-white p-5 flex flex-wrap items-center gap-4">
          <Books size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Explore</div>
            <div className="mt-0.5 font-serif-display text-lg">Crop database, calendars and community.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { to: "/farmers/crops", label: "Crop database", testid: "farmers-explore-crops", guide: "farmers-tab-crops" },
              { to: "/farmers/calendar", label: "Planting calendar", testid: "farmers-explore-calendar", guide: "farmers-tab-calendar" },
              { to: "/farmers/pest-heatmap", label: "Pest heatmap", testid: "farmers-explore-heatmap", guide: "farmers-tab-pest-heatmap" },
              { to: "/farmers/farms", label: "My farms", testid: "farmers-explore-my-farms", guide: "farmers-tab-my-farms" },
              { to: "/farmers/inventory", label: "Inventory", testid: "farmers-explore-inventory", guide: "farmers-tab-inventory" },
              { to: "/farmers/websites", label: "Build a website", testid: "farmers-explore-websites", guide: "farmers-tab-websites" },
              { to: "/farmers/report", label: "Report what you see", testid: "farmers-explore-report", guide: "farmers-tab-report", primary: true },
            ].map((c) => (
              <div key={c.testid} className="text-center">
                <Link to={c.to} data-testid={c.testid}
                  className={`rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase inline-block ${
                    c.primary ? "bg-[#1B4332] text-[#F5EBDC] hover:opacity-90" : "border border-black/15 bg-white hover:bg-black/5"}`}>
                  {c.label}
                </Link>
                <Link to={`/read/articles/${c.guide}`} data-testid={`${c.testid}-guide`}
                  className="mt-1 block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
                  AI guide →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10 pb-4">
        <RelatedReading tag="farmers-pulse" testid="farmers-related" />
      </section>
    </main>
  );
}

/**
 * Renders one card based on its module key. The switch keeps the layout
 * for each card local — every module is independent and can be toggled
 * off in the admin without breaking the grid.
 */
function ModuleCard({ module, loc, calendar, nearby, myFarms, signedIn }) {
  const Icon = ICON_MAP[module.icon] || Sparkle;
  const shell = "rounded-2xl border border-black/10 bg-white p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition h-full flex flex-col";
  const meta = (
    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
      <Icon size={12} weight="fill" /> {module.name}
    </div>
  );

  switch (module.key) {
    case "weather-today":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-3xl leading-tight">Today's sky</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Local temperature, humidity, wind and rainfall. Uses the NaturePulse Pulse dashboard as the underlying source.
          </p>
          <Link
            to="/pulse"
            data-testid={`farmers-card-${module.key}-cta`}
            className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            Open weather Pulse <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "rainfall":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Rainfall watch</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Today, this week and this season rainfall totals — with historical baselines so you can spot dry spells early.
          </p>
          <Link to="/pulse" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Open rain Pulse <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "growing-conditions":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Growing conditions</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Weather translated into farmer guidance — soil workability, transplant risk, spray windows. Marked as AI interpretation, never as verified fact.
          </p>
          <Link to="/pulse" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            See conditions <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "farm-alerts":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Farm alerts</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Frost, drought, heat, storm and pest early-warnings for your region — pulled from NaturePulse observations and weather feeds.
          </p>
          <Link to="/notifications" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Open alerts <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "irrigation":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Irrigation</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Suggested irrigation windows based on rainfall received and short-term evapotranspiration for your area.
          </p>
          <Link to="/farmers/farms" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Set up on my farm <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "farm-calendar":
    case "what-to-plant":
    case "what-to-harvest": {
      const key = module.key;
      const filterKind = key === "what-to-plant" ? "sow" : key === "what-to-harvest" ? "harvest" : null;
      const items = (calendar?.crops || []).filter((c) => !filterKind || c.what_to_do_now === filterKind).slice(0, 6);
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">
            {key === "what-to-plant" ? "Sow this week" : key === "what-to-harvest" ? "Ready to harvest" : "Your crop calendar"}
          </div>
          {!loc?.lat && (
            <p className="mt-2 text-sm text-[#3A3830]">Set your location above to see a local calendar.</p>
          )}
          {loc?.lat && !calendar && (
            <p className="mt-2 text-sm text-[#3A3830]">Loading calendar…</p>
          )}
          {loc?.lat && calendar && items.length === 0 && (
            <p className="mt-2 text-sm text-[#3A3830]">
              No {key === "what-to-plant" ? "planting" : "harvest"} windows for {calendar?.climate_zone || "your region"} this month.
            </p>
          )}
          {items.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {items.map((c) => (
                <li key={c.slug} className="flex items-center gap-2">
                  <span className="text-lg">{c.emoji || "🌱"}</span>
                  <Link to={`/farmers/crops/${c.slug}`} className="font-serif-display text-base hover:text-[color:var(--np-brand,#B85F2A)]">
                    {c.common_name}
                  </Link>
                  <span className="ml-auto rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                    {c.what_to_do_now}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {calendar && (
            <div className="mt-3 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
              {calendar.climate_zone} · {calendar.hemisphere} hemisphere · month {calendar.current_month}
            </div>
          )}
          <Link to="/farmers/calendar" className="mt-auto pt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Full calendar <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );
    }

    case "nearby-reports":
    case "pest-disease-alerts": {
      const filterCat = module.key === "pest-disease-alerts" ? ["pest", "disease"] : null;
      const items = (nearby || []).filter((n) => {
        if (!filterCat) return true;
        return filterCat.includes(n.category) || (n.labels || []).some((l) => filterCat.includes(l));
      }).slice(0, 5);
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">
            {module.key === "pest-disease-alerts" ? "Pest & disease pressure near you" : "What's happening around you"}
          </div>
          {!loc?.lat && <p className="mt-2 text-sm text-[#3A3830]">Set your location above to see nearby reports.</p>}
          {loc?.lat && items.length === 0 && <p className="mt-2 text-sm text-[#3A3830]">No reports within 50 km yet — be the first.</p>}
          {items.map((n) => (
            <Link
              key={n.id}
              to={`/observations/${n.id}`}
              data-testid={`farmers-nearby-${n.id}`}
              className="mt-3 rounded-lg border border-black/10 p-3 hover:border-[color:var(--np-brand,#B85F2A)] transition block"
            >
              <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {n.category} · {n.km} km
                {n.severity && <><span className="text-[#3A3830]"> · </span>{n.severity}</>}
              </div>
              <div className="mt-1 font-serif-display text-base leading-tight line-clamp-2">{n.title || "Untitled report"}</div>
              {n.description && <div className="mt-1 text-xs text-[#3A3830] line-clamp-2">{n.description}</div>}
            </Link>
          ))}
          <Link
            to={module.key === "pest-disease-alerts" ? "/farmers/pest-heatmap" : "/farmers/report"}
            data-testid={`farmers-card-${module.key}-cta`}
            className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            {module.key === "pest-disease-alerts" ? "Open pest heatmap" : "Add a report"} <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );
    }

    case "my-farm":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Your farms</div>
          {!signedIn ? (
            <p className="mt-2 text-sm text-[#3A3830]">Sign in to create and manage private or public farm records.</p>
          ) : myFarms.length === 0 ? (
            <p className="mt-2 text-sm text-[#3A3830]">You haven't added a farm yet. Farms unlock your diary and personalized recommendations.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {myFarms.slice(0, 3).map((f) => (
                <li key={f.id}>
                  <Link to={`/farmers/farms/${f.id}`} className="rounded-lg border border-black/10 p-3 block hover:border-[color:var(--np-brand,#B85F2A)]">
                    <div className="font-serif-display text-base leading-tight">{f.name}</div>
                    <div className="mt-0.5 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[#3A3830]">
                      {[f.village, f.district, f.state, f.country].filter(Boolean).join(", ") || "Location not set"}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-auto pt-3 flex gap-2">
            <Link to="/farmers/farms" className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">All farms</Link>
            <Link to="/farmers/farms/new" className="ml-auto font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">+ New farm</Link>
          </div>
        </div>
      );

    case "farm-diary":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Your farm diary</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            A daily journal for planting, weather, pests, pollinators and harvests. Records stay private by default.
          </p>
          <Link
            to={myFarms[0] ? `/farmers/farms/${myFarms[0].id}` : "/farmers/farms"}
            data-testid={`farmers-card-${module.key}-cta`}
            className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            {myFarms[0] ? "Open my diary" : "Set up your first farm"} <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "agri-news":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Agricultural news</div>
          <p className="mt-2 text-sm text-[#3A3830]">Curated feeds on agriculture, plant science, water and climate change — with original sources preserved.</p>
          <Link to="/news?category=agriculture" className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Open news <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "ask-ai":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Ask Farmers Pulse</div>
          <p className="mt-2 text-sm text-[#3A3830]">Your farm advisor — sowing windows, soil, prices and weather. Live on every page via the floating Ask button, or open the full desk.</p>
          <Link to="/farmers/ask" className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={10} weight="fill" /> Open the advisor
          </Link>
        </div>
      );

    default:
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">{module.name}</div>
          {module.description && <p className="mt-2 text-sm text-[#3A3830]">{module.description}</p>}
          <span className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
            Enabled
          </span>
        </div>
      );
  }
}
